import { useEffect, useState, useCallback, useMemo, type DragEvent } from "react";
import { appendUnique, firstPage, listedRows, nextPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Card, Button, Cluster, Drawer, EmptyState, BoardColumn, KanbanCard, KanbanStack, StageFunnel, GroupTree, Input, Select, Stack, useToast } from "@salesapp/ui";
import { crmApi, type Lead, type LeadBoard, type BoardDisplaySettings } from "../../lib/crmApi";
import { ACTION_HINTS, FIELD_HINTS, LEAD_STATUS_OPTIONS, LEAD_TEMPERATURE_OPTIONS, statusTitle } from "../../help/fieldHints";
import { useAccess } from "../../shell/AccessProvider";
import { LeadCaptureForm } from "./LeadCaptureForm";
import { ListWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { GroupByControl } from "./GroupByControl";
import { SearchBar, StatusSlicer, statusLabel, useSelectOptions } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { useCrmHref } from "../../lib/customerUrl";
import { DisqualifyLeadModal } from "./DisqualifyLeadModal";
import { ResponsiveDataTable, type DataColumn, type DataRow } from "./ResponsiveDataTable";
import { useLocale } from "../../shell/LocaleProvider";
import { leadPersonName, personCompanyTitle } from "./recordLabels";
import { BoardDisplaySettingsPanel } from "./BoardDisplaySettings";
import { TabFieldOptionsSettings } from "./TabFieldOptionsSettings";
import { agingBackground, terminalNegativeBackground, terminalPositiveBackground } from "./boardDisplayColors";
import { opsApi } from "../../lib/opsApi";
import {
  bucketProjectedPipeline,
  projectedChartPoints,
  type ProjectedBucket,
  type ProjectedGrain,
} from "./projectedPipeline";
import { ProjectedLandingChart } from "./ProjectedLandingChart";
import { AskLoxleyButton } from "../../loxley/AskLoxleyButton";

type GroupBy = "none" | "salesperson" | "company" | "contact";

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "none", label: "No grouping" },
  { value: "salesperson", label: "Salesperson" },
  { value: "company", label: "Company" },
  { value: "contact", label: "Contact" },
];

function leadGroupKeyFor(lead: Lead, groupBy: GroupBy): { key: string; label: string } {
  if (groupBy === "salesperson") return { key: lead.owner_user_id ?? "—", label: lead.owner_name ?? "Unassigned" };
  if (groupBy === "contact") return { key: lead.id, label: leadPersonName(lead) || "No contact" };
  return { key: lead.company_id ?? "—", label: lead.company_name ?? "Unknown company" };
}

export function groupCardsBy<T extends { amount?: number | null; value?: number | null; currency?: string | null }>(
  items: T[],
  groupBy: GroupBy,
  keyFor: (item: T, groupBy: GroupBy) => { key: string; label: string },
): { key: string; label: string; items: T[]; total: number; currency: string | null }[] {
  const byKey = new Map<string, { key: string; label: string; items: T[]; total: number; currency: string | null }>();
  for (const item of items) {
    const { key, label } = keyFor(item, groupBy);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = { key, label, items: [], total: 0, currency: item.currency ?? null };
      byKey.set(key, bucket);
    }
    bucket.items.push(item);
    bucket.total += item.amount ?? item.value ?? 0;
  }
  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
}

const CONVERTED = "converted";
const DISQUALIFIED = "disqualified";
const LEAD_STATUSES = ["new", "working", "qualified", "converted", "disqualified"];

type LeadView = "board" | "table" | "projected";

function readLeadView(raw: string | null): LeadView {
  if (raw === "table" || raw === "projected") return raw;
  return "board";
}

function readGrain(raw: string | null): ProjectedGrain {
  return raw === "quarter" ? "quarter" : "month";
}

const EMPTY_PROJECTED: ProjectedBucket<Lead>[] = [];

function openLeads(items: Lead[]): Lead[] {
  return items.filter((item) => item.lead_status !== CONVERTED && item.lead_status !== DISQUALIFIED);
}

function leadValueOf(lead: Lead): number | null | undefined {
  return lead.amount;
}

function leadName(lead: Lead): string {
  return personCompanyTitle(leadPersonName(lead), lead.company_name, "Untitled lead");
}

function isConvertedLead(lead: Lead): boolean {
  return Boolean(lead.converted_at) || lead.lead_status === CONVERTED;
}

function isDisqualifiedLead(lead: Lead): boolean {
  return lead.lead_status === DISQUALIFIED;
}

function isTerminalLead(lead: Lead): boolean {
  return isConvertedLead(lead) || isDisqualifiedLead(lead);
}

function leadCardBackground(lead: Lead, settings: BoardDisplaySettings | null | undefined): string | undefined {
  if (isConvertedLead(lead)) return terminalPositiveBackground(settings?.terminal_positive_color);
  if (isDisqualifiedLead(lead)) return terminalNegativeBackground(settings?.terminal_negative_color);
  return agingBackground(settings, lead.age_days);
}

function leadColumnTotal(cards: Lead[], money: (value: number | null | undefined, currency?: string | null) => string | undefined): string | undefined {
  const total = cards.reduce((sum, lead) => sum + (lead.amount ?? 0), 0);
  return total ? money(total, cards.find((lead) => lead.currency)?.currency) : undefined;
}

function convertedLeadHref(lead: Lead, crmHref: (path: string) => string): string {
  if (isConvertedLead(lead) && lead.converted_to_opportunity_id) {
    return crmHref(`/opportunities/${lead.converted_to_opportunity_id}`);
  }
  return crmHref(`/leads/${lead.id}`);
}

function convertedLeadStatusLabel(lead: Lead): string {
  const stage = lead.opportunity_stage_label || lead.opportunity_stage;
  if (stage) return `Lead convert: Opportunity stage ${stage}`;
  return lead.lead_status ?? "converted";
}

function ageLabel(days: number | null | undefined): string | null {
  if (days == null) return null;
  if (days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function ratingClass(rating: string | null | undefined): string {
  if (rating === "hot") return "crm-badge crm-badge--hot";
  if (rating === "warm") return "crm-badge crm-badge--warm";
  return "crm-badge crm-badge--cold";
}

export function LeadsPage({ orgId }: { orgId: string }) {
  const { can } = useAccess();
  const { show } = useToast();
  const { formatMoney, formatDate, fiscalYearStartMonth } = useLocale();
  const money = (value: number | null | undefined, currency?: string | null) =>
    value == null ? undefined : formatMoney(value, currency, true);
  const crmHref = useCrmHref();
  const canMove = can("crm.leads", "update");
  const canConfigure = can("crm.leads", "configure");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [board, setBoard] = useState<LeadBoard | null>(null);
  const [boardDisplay, setBoardDisplay] = useState<BoardDisplaySettings | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [territoryFilter, setTerritoryFilter] = useState("");
  const [territories, setTerritories] = useState<Array<{ id: string; name: string }>>([]);
  const [showForm, setShowForm] = useState(false);
  const [overStatus, setOverStatus] = useState<string | null>(null);
  const [draggingLeadId, setDraggingLeadId] = useState<string | null>(null);
  const [pendingDisqualify, setPendingDisqualify] = useState<Lead | null>(null);
  const [convertDraft, setConvertDraft] = useState<Lead | null>(null);
  const [convertValue, setConvertValue] = useState("");
  const [disqualifyComment, setDisqualifyComment] = useState("");
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [periodTargets, setPeriodTargets] = useState<Record<string, number>>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const view = readLeadView(searchParams.get("view"));
  const grain = readGrain(searchParams.get("grain"));
  const ratingFilter = searchParams.get("rating") ?? "";
  const openOnly = searchParams.get("open") === "1";
  const setRating = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set("rating", next);
    else params.delete("rating");
    setSearchParams(params, { replace: true });
  };
  const clearOpen = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("open");
    setSearchParams(params, { replace: true });
  };
  const canForecast = can("crm.forecast");
  const statusOptions = useSelectOptions(orgId, "lead", "lead_status", LEAD_STATUSES);

  const setView = (next: LeadView) => {
    const params = new URLSearchParams(searchParams);
    if (next === "board") params.delete("view");
    else params.set("view", next);
    if (next !== "projected") params.delete("grain");
    else if (!params.get("grain")) params.set("grain", "month");
    setSearchParams(params, { replace: true });
  };

  const setGrain = (next: ProjectedGrain) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", "projected");
    if (next === "month") params.delete("grain");
    else params.set("grain", next);
    setSearchParams(params, { replace: true });
  };

  const projectedBuckets = useMemo(
    () =>
      view === "projected"
        ? bucketProjectedPipeline(openLeads(leads), grain, new Date(), fiscalYearStartMonth, leadValueOf)
        : EMPTY_PROJECTED,
    [view, leads, grain, fiscalYearStartMonth],
  );
  const chartPoints = useMemo(
    () =>
      projectedChartPoints(
        projectedBuckets,
        (key) => {
          if (periodTargets[key] != null) return periodTargets[key];
          const year = Number(key.slice(0, 4));
          if (!year || !periodTargets[`year:${year}`]) return 0;
          return grain === "month" ? periodTargets[`year:${year}`] / 12 : periodTargets[`year:${year}`] / 4;
        },
        leadValueOf,
      ),
    [projectedBuckets, periodTargets, grain],
  );

  useEffect(() => {
    if (searchParams.get("companyId") || searchParams.get("companyName")) {
      setShowForm(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (view !== "projected" || !canForecast) {
      setPeriodTargets({});
      return;
    }
    const years = [
      ...new Set(
        projectedBuckets
          .filter((bucket) => bucket.key !== "unscheduled")
          .map((bucket) => Number(bucket.key.slice(0, 4)))
          .filter(Boolean),
      ),
    ];
    if (years.length === 0) {
      const year = new Date().getUTCFullYear();
      years.push(year, year + 1);
    }
    let cancelled = false;
    void (async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        years.map(async (year) => {
          try {
            const forecast = await opsApi.forecast(orgId, year, "detail");
            const yearly = Number(forecast.target_total) || 0;
            next[`year:${year}`] = yearly;
            for (const person of forecast.people ?? []) {
              for (const month of person.months ?? []) {
                const key = `${year}-${String(month.month).padStart(2, "0")}`;
                next[key] = (next[key] ?? 0) + (Number(month.target) || 0);
              }
              for (const quarter of person.quarters ?? []) {
                const key = `${year}-Q${quarter.quarter}`;
                next[key] = (next[key] ?? 0) + (Number(quarter.target) || 0);
              }
            }
            if (!(forecast.people && forecast.people.length > 0) && yearly > 0) {
              for (let month = 1; month <= 12; month += 1) {
                next[`${year}-${String(month).padStart(2, "0")}`] = yearly / 12;
              }
              for (let quarter = 1; quarter <= 4; quarter += 1) {
                next[`${year}-Q${quarter}`] = yearly / 4;
              }
            }
          } catch {
            /* Forecast is optional on this view — chart still shows projected $. */
          }
        }),
      );
      if (!cancelled) setPeriodTargets(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [view, canForecast, orgId, projectedBuckets]);

  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(
        await crmApi.listLeads(orgId, statusFilter || undefined, search || undefined, undefined, nextPage(leads.length), territoryFilter || undefined, {
          withTotal: true,
          rating: ratingFilter || undefined,
          openOnly,
        }),
      );
      const page = trimPage(listed.rows);
      setLeads((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more leads");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasMore(false);
    try {
      const leadExtra = { rating: ratingFilter || undefined, openOnly };
      if (view === "board") {
        const data = ratingFilter || openOnly
          ? await crmApi.listLeadBoard(orgId, search || undefined, territoryFilter || undefined, leadExtra)
          : await crmApi.listLeadBoard(orgId, search || undefined, territoryFilter || undefined);
        setBoard(data);
        setBoardDisplay(data.board_display ?? null);
        setLeads(Object.values(data.buckets).flat());
        setTotal(data.total ?? null);
      } else {
        const listed = listedRows(
          await crmApi.listLeads(
            orgId,
            statusFilter || undefined,
            search || undefined,
            undefined,
            firstPage(),
            territoryFilter || undefined,
            { ...leadExtra, withTotal: true },
          ),
        );
        const page = trimPage(listed.rows);
        setLeads(page.items);
        setHasMore(page.hasMore);
        setTotal(listed.total);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [orgId, search, statusFilter, territoryFilter, view, ratingFilter, openOnly]);

  useEffect(() => {
    void opsApi.listTerritories(orgId).then((row) => {
      setTerritories(row.territories.map((item) => ({ id: item.id, name: item.name })));
    }).catch(() => setTerritories([]));
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const moveLead = async (lead: Lead, nextStatus: string, comment?: string, notify?: boolean) => {
    const current = lead.lead_status ?? "new";
    if (nextStatus === current) return;
    if (nextStatus === CONVERTED) {
      await convertLead(lead, { navigateToOpportunity: false });
      return;
    }
    if (nextStatus === "disqualified" && !comment?.trim()) {
      setPendingDisqualify(lead);
      setDisqualifyComment("");
      return;
    }
    const previous = board;
    if (board) {
      const nextBuckets: Record<string, Lead[]> = {};
      for (const [status, cards] of Object.entries(board.buckets)) {
        nextBuckets[status] = cards.filter((card) => card.id !== lead.id);
      }
      const moved = { ...lead, lead_status: nextStatus };
      nextBuckets[nextStatus] = [...(nextBuckets[nextStatus] ?? []), moved];
      setBoard({ ...board, buckets: nextBuckets });
    }
    try {
      await crmApi.changeLeadStatus(lead.id, nextStatus, orgId, comment?.trim() || undefined);
      if (notify) show("Saved!", "success");
    } catch (e) {
      if (previous) setBoard(previous);
      setError(e instanceof Error ? e.message : "Failed to move lead");
    }
  };

  const handleDrop = (status: string) => async (event: DragEvent) => {
    event.preventDefault();
    setOverStatus(null);
    const leadId = event.dataTransfer.getData("text/plain") || draggingLeadId;
    const source = Object.values(board?.buckets ?? {}).flat().find((card) => card.id === leadId);
    if (!source) {
      setDraggingLeadId(null);
      return;
    }
    await moveLead(source, status);
    setDraggingLeadId(null);
  };

  const cardMoveStatuses = board?.stages ?? [];

  const convertLead = async (lead: Lead, options: { navigateToOpportunity?: boolean } = {}) => {
    const known = lead.amount != null && lead.amount > 0 ? lead.amount : null;
    if (known == null) {
      setConvertDraft(lead);
      setConvertValue("");
      setError("An opportunity needs a value. Enter the deal value, then convert.");
      return;
    }
    await finishConvert(lead, known, options);
  };

  const finishConvert = async (lead: Lead, dealValue: number, options: { navigateToOpportunity?: boolean } = {}) => {
    const previous = board;
    try {
      const result = await crmApi.convertLead(
        lead.id, true, undefined, orgId, dealValue, lead.currency || undefined,
      );
      setConvertDraft(null);
      show("Converted to opportunity", "success");
      const convertedLead = {
        ...lead,
        lead_status: CONVERTED,
        converted_at: lead.converted_at ?? new Date().toISOString(),
        converted_to_company_id: result.company_id ?? lead.converted_to_company_id,
        converted_to_contact_id: result.contact_id ?? lead.converted_to_contact_id,
        converted_to_opportunity_id: result.opportunity_id ?? lead.converted_to_opportunity_id,
      };
      if (board) {
        const nextBuckets: Record<string, Lead[]> = {};
        for (const [status, cards] of Object.entries(board.buckets)) {
          nextBuckets[status] = cards.filter((card) => card.id !== lead.id);
        }
        nextBuckets[CONVERTED] = [...(nextBuckets[CONVERTED] ?? []), convertedLead];
        setBoard({ ...board, buckets: nextBuckets });
      }
      setLeads((rows) => rows.map((row) => (row.id === lead.id ? convertedLead : row)));
      if (options.navigateToOpportunity !== false && result.opportunity_id) {
        navigate(crmHref(`/opportunities/${result.opportunity_id}`));
        return;
      }
      if (!board) await load();
    } catch (e) {
      if (previous) setBoard(previous);
      setError(e instanceof Error ? e.message : "Failed to convert lead");
    }
  };

  const leadColumns: DataColumn[] = [
    { key: "id", label: "ID", hint: "System-generated lead reference." },
    { key: "name", label: "Name", primary: true, hint: "Contact name for this lead." },
    { key: "company", label: "Company", primary: true, hint: FIELD_HINTS.lead_company },
    { key: "amount", label: "Amount", primary: true, hint: "Estimated deal value for this lead, if set." },
    { key: "email", label: "Email", hint: FIELD_HINTS.lead_email },
    { key: "temperature", label: "Temperature", primary: true, hint: FIELD_HINTS.lead_temperature },
    { key: "status", label: "Status", primary: true, hint: FIELD_HINTS.lead_status },
  ];

  const buildLeadRow = (l: Lead): DataRow => {
    const converted = isConvertedLead(l);
    const terminal = isTerminalLead(l);
    const href = convertedLeadHref(l, crmHref);
    const statusValue = converted ? convertedLeadStatusLabel(l) : (l.lead_status ?? "new");
    return {
      id: l.id,
      href,
      className: terminal ? "is-pipeline-muted" : undefined,
      cells: {
        id: l.lead_code ?? "—",
        name: (
          <Link to={href}>
            {leadName(l)}
          </Link>
        ),
        company: l.company_id ? (
          <Link to={crmHref(`/companies/${l.company_id}`)}>{l.company_name ?? "Company"}</Link>
        ) : (
          (l.company_name ?? "—")
        ),
        amount: money(l.amount, l.currency) ?? "—",
        email: l.email ?? "—",
        temperature: (
          <span className={ratingClass(l.rating)} title={statusTitle(LEAD_TEMPERATURE_OPTIONS, l.rating ?? "cold")}>
            {l.rating ?? "cold"}
          </span>
        ),
        status: (
          <StatusBadge status={statusValue} options={LEAD_STATUS_OPTIONS} className="crm-badge" />
        ),
      },
      actions: (
        <Cluster gap={2}>
          <AskLoxleyButton record={{ kind: "lead", id: l.id, label: leadName(l) }} />
          {!l.converted_at && (
            <Button
              variant="ghost"
              title="Convert this lead into an opportunity (and company/contact when needed)."
              onClick={() => void convertLead(l)}
            >
              Convert
            </Button>
          )}
          <DeleteRecordButton
            capability="crm.leads"
            recordLabel={leadName(l)}
            size="sm"
            variant="ghost"
            onDeleted={async () => {
              try {
                await crmApi.deleteLead(l.id, orgId);
                await load();
              } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to delete lead");
              }
            }}
          />
        </Cluster>
      ),
    };
  };

  const groupedLeads = useMemo(() => {
    if (groupBy === "none") return [];
    return groupCardsBy(leads, groupBy, leadGroupKeyFor);
  }, [leads, groupBy]);

  const renderLeadCard = (lead: Lead) => {
    const converted = isConvertedLead(lead);
    const terminal = isTerminalLead(lead);
    const href = convertedLeadHref(lead, crmHref);
    return (
      <KanbanCard
        key={lead.id}
        title={leadName(lead)}
        subtitle={lead.lead_code || undefined}
        href={href}
        value={money(lead.amount, lead.currency)}
        muted={terminal}
        backgroundColor={leadCardBackground(lead, boardDisplay)}
        draggable={canMove && !terminal}
        onDragStart={(event) => {
          setDraggingLeadId(lead.id);
          event.dataTransfer.setData("text/plain", lead.id);
          event.dataTransfer.effectAllowed = "move";
        }}
        onDragEnd={() => {
          setOverStatus(null);
          setDraggingLeadId(null);
        }}
        onClick={() => navigate(href)}
        tags={
          <span className="crm-kanban-card-meta crm-kanban-card-meta--board">
            <span className="crm-kanban-card-meta__main">
              {converted ? (
                <span className="crm-badge crm-badge--board-action">{convertedLeadStatusLabel(lead)}</span>
              ) : null}
              {!terminal && canMove && (
                <select
                  className="crm-select crm-kanban-status"
                  aria-label={`Move ${leadName(lead)}`}
                  value={lead.lead_status ?? "new"}
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    event.stopPropagation();
                    void moveLead(lead, event.target.value, undefined, true);
                  }}
                >
                  {cardMoveStatuses.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </span>
            {!terminal && (
              <span className="crm-badge crm-badge--aging">{ageLabel(lead.age_days) ?? "Today"}</span>
            )}
            {!terminal && (
              <span className={`${ratingClass(lead.rating)} crm-badge--rating-lg`}>{lead.rating ?? "cold"}</span>
            )}
            {!terminal && lead.next_action_at ? (
              <span className="crm-badge">
                {lead.next_action_type ? `${statusLabel(lead.next_action_type)} · ` : "Next "}
                {formatDate(lead.next_action_at)}
              </span>
            ) : null}
          </span>
        }
      />
    );
  };

  return (
    <ListWorkspace
      title="Leads"
      actions={
        <ResponsiveActions
          primary={
            <>
              <div className="crm-view-toggle" role="group" aria-label="Lead view">
                <button type="button" className={view === "board" ? "is-active" : ""} onClick={() => setView("board")}>
                  Board
                </button>
                <button type="button" className={view === "projected" ? "is-active" : ""} onClick={() => setView("projected")}>
                  Projected
                </button>
                <button type="button" className={view === "table" ? "is-active" : ""} onClick={() => setView("table")}>
                  Table
                </button>
              </div>
              <Button onClick={() => setShowForm(!showForm)} data-tour="new-lead">{showForm ? "Cancel" : "New lead"}</Button>
            </>
          }
          more={
            <>
              {territories.length > 0 && (
                <Select
                  aria-label="Territory"
                  value={territoryFilter}
                  onChange={(event) => setTerritoryFilter(event.target.value)}
                >
                  <option value="">All territories</option>
                  {territories.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </Select>
              )}
              {canConfigure ? (
                <Button type="button" variant="secondary" onClick={() => setSettingsOpen(true)}>
                  Lead settings
                </Button>
              ) : null}
            </>
          }
        />
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search leads…"
            label="Search leads"
            hint="Search by company, contact name, email, phone, source, notes, or salesperson."
          />
          {(view === "board" || view === "table") && (
            <StatusSlicer
              value={ratingFilter}
              onChange={setRating}
              options={["hot", "warm", "cold"]}
              label="Filter by temperature"
              optionHints={LEAD_TEMPERATURE_OPTIONS}
              title="Show leads at this temperature."
            />
          )}
          {openOnly ? (
            <Cluster gap={2}>
              <p className="crm-help">Showing open leads.</p>
              <Button type="button" variant="ghost" size="sm" onClick={clearOpen}>
                Show converted and disqualified too
              </Button>
            </Cluster>
          ) : null}
          {view === "table" && (
            <StatusSlicer
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              optionHints={LEAD_STATUS_OPTIONS}
              title={ACTION_HINTS.filter_lead_status}
            />
          )}
          {view === "projected" && (
            <div className="crm-view-toggle" role="group" aria-label="Projected period">
              <button type="button" className={grain === "month" ? "is-active" : ""} onClick={() => setGrain("month")}>
                By month
              </button>
              <button type="button" className={grain === "quarter" ? "is-active" : ""} onClick={() => setGrain("quarter")}>
                By quarter
              </button>
            </div>
          )}
          {(view === "table" || view === "board") && (
            <GroupByControl
              label="Group leads by"
              value={groupBy}
              options={GROUP_BY_OPTIONS}
              onChange={(next) => setGroupBy(next as GroupBy)}
            />
          )}
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {convertDraft && (
        <Card>
          <Stack gap={3}>
            <h2 className="home-section-title">Deal value for {leadName(convertDraft)}</h2>
            <p className="home-subtitle">An opportunity has to have a value. Enter it, then convert.</p>
            <Input
              type="number"
              min={0}
              aria-label="Deal value"
              value={convertValue}
              onChange={(event) => setConvertValue(event.target.value)}
            />
            <Cluster gap={2}>
              <Button
                onClick={() => {
                  const deal = Number(convertValue);
                  if (!Number.isFinite(deal) || deal <= 0) {
                    setError("Enter a deal value greater than zero.");
                    return;
                  }
                  void finishConvert(convertDraft, deal);
                }}
              >
                Convert
              </Button>
              <Button variant="ghost" onClick={() => setConvertDraft(null)}>Cancel</Button>
            </Cluster>
          </Stack>
        </Card>
      )}

      {showForm && (
        <div className="crm-panel-card">
          <h2 className="crm-section-title">New lead</h2>
          <LeadCaptureForm
            orgId={orgId}
            initial={{
              company_name: searchParams.get("companyName") ?? "",
              company_id: searchParams.get("companyId") ?? "",
            }}
            submitLabel="Create lead"
            onCreated={(res) => {
              setShowForm(false);
              navigate(crmHref(`/leads/${res.id}`));
            }}
          />
        </div>
      )}

      {loading ? (
        <Card>Loading...</Card>
      ) : view === "board" ? (
        !board || leads.length === 0 ? (
          <Card>
            <EmptyState title="No leads found" description="Type a company name as you create a lead. If it does not exist yet, we will create an incomplete company with it." />
          </Card>
        ) : (
          <>
          <StageFunnel
            segments={board.stages.map((stage) => ({
              value: stage.value,
              label: stage.label,
              count: (board.buckets[stage.value] ?? []).length,
              negative: stage.value === "disqualified",
            }))}
            onSegmentDragEnter={canMove ? (status) => setOverStatus(status) : undefined}
            onSegmentDrop={
              canMove
                ? (status, event) => {
                    void handleDrop(status)(event);
                  }
                : undefined
            }
          />
          <div className="crm-kanban" data-testid="leads-board" role="list" aria-label="Lead board">
            {board.stages.map((stage) => {
              const cards = board.buckets[stage.value] ?? [];
              const groups = groupBy === "none" ? [] : groupCardsBy(cards, groupBy, leadGroupKeyFor);
              return (
                <BoardColumn
                  key={stage.value}
                  className={`crm-kanban-column${overStatus === stage.value ? " is-over" : ""}`}
                  title={stage.label}
                  count={cards.length}
                  totalValue={leadColumnTotal(cards, money)}
                  onDragEnter={canMove ? () => setOverStatus(stage.value) : undefined}
                  onDrop={(event) => void handleDrop(stage.value)(event)}
                >
                  {cards.length === 0 ? (
                    <p className="crm-kanban-empty">
                      {stage.value === CONVERTED ? "Drop a lead here to convert it." : "Drop a lead here."}
                    </p>
                  ) : groupBy === "none" ? (
                    cards.map((lead) => renderLeadCard(lead))
                  ) : (
                    groups.map((group) => (
                      <KanbanStack
                        key={group.key}
                        label={group.label}
                        count={group.items.length}
                        totalValue={group.total ? money(group.total, group.currency) : undefined}
                      >
                        {group.items.map((lead) => renderLeadCard(lead))}
                      </KanbanStack>
                    ))
                  )}
                </BoardColumn>
              );
            })}
          </div>
          </>
        )
      ) : view === "projected" ? (
        leads.length === 0 && projectedBuckets.every((bucket) => bucket.cards.length === 0) ? (
          <Card>
            <EmptyState
              title="No projected landings yet"
              description="Add expected close dates on open leads to see when they would land if converted."
            />
          </Card>
        ) : (
          <>
            <Card className="projected-chart-card">
              <ProjectedLandingChart
                points={chartPoints}
                grainLabel={grain === "month" ? "month, next 6 months" : "quarter over the next 6 months"}
              />
            </Card>
            <StageFunnel
              segments={projectedBuckets.map((bucket) => ({
                value: bucket.key,
                label: bucket.label,
                count: bucket.cards.length,
                negative: bucket.key === "unscheduled",
              }))}
            />
            <div className="crm-kanban" data-testid="leads-projected" role="list" aria-label="Projected landing">
              {projectedBuckets.map((bucket) => (
                <BoardColumn
                  key={bucket.key}
                  className="crm-kanban-column"
                  title={bucket.label}
                  count={bucket.cards.length}
                  totalValue={
                    bucket.cards.reduce((sum, card) => sum + (card.amount ?? 0), 0)
                      ? money(bucket.cards.reduce((sum, card) => sum + (card.amount ?? 0), 0))
                      : undefined
                  }
                >
                  {bucket.cards.length === 0 ? (
                    <p className="crm-kanban-empty">No leads expected to close in this period.</p>
                  ) : (
                    bucket.cards.map((lead) => (
                      <KanbanCard
                        key={lead.id}
                        title={leadName(lead)}
                        subtitle={lead.lead_code || undefined}
                        value={money(lead.amount, lead.currency)}
                        href={crmHref(`/leads/${lead.id}`)}
                        onClick={() => navigate(crmHref(`/leads/${lead.id}`))}
                        tags={
                          <span className="crm-kanban-card-meta">
                            {ageLabel(lead.age_days) && (
                              <span className="crm-badge crm-badge--aging">{ageLabel(lead.age_days)}</span>
                            )}
                            {lead.expected_close_date && (
                              <span className="crm-badge">{lead.expected_close_date.slice(0, 10)}</span>
                            )}
                          </span>
                        }
                      />
                    ))
                  )}
                </BoardColumn>
              ))}
            </div>
            <p className="crm-help">
              Converted and Disqualified leads are excluded. Leads without an expected close date sit in No close
              date and are left off the chart. Set salesperson targets under Forecast & KPI to draw the target
              tick on each bar.
            </p>
          </>
        )
      ) : leads.length === 0 ? (
        <Card>
          <EmptyState title="No leads found" description="Type a company name as you create a lead. If it does not exist yet, we will create an incomplete company with it." />
        </Card>
      ) : groupBy === "none" ? (
        <Card className="crm-table-card">
          <ResponsiveDataTable caption="Leads" columns={leadColumns} rows={leads.map(buildLeadRow)} />
        </Card>
      ) : (
        <GroupTree
          groups={groupedLeads.map((group) => ({
            key: group.key,
            label: group.label,
            count: group.items.length,
            totalValue: group.total ? money(group.total, group.currency) : undefined,
            children: (
              <ResponsiveDataTable caption={`Leads — ${group.label}`} columns={leadColumns} rows={group.items.map(buildLeadRow)} />
            ),
          }))}
        />
      )}
      <DisqualifyLeadModal
        open={Boolean(pendingDisqualify)}
        comment={disqualifyComment}
        onCommentChange={setDisqualifyComment}
        onCancel={() => setPendingDisqualify(null)}
        onConfirm={() => {
          if (!pendingDisqualify) return;
          const lead = pendingDisqualify;
          setPendingDisqualify(null);
          void moveLead(lead, "disqualified", disqualifyComment.trim(), true);
        }}
      />
      {view === "table" && (
        <LoadMore shown={leads.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="leads" />
      )}
      <Drawer
        isOpen={settingsOpen}
        onClose={() => {
          setSettingsOpen(false);
          void load();
        }}
        title="Lead settings"
      >
        {can("crm.field_options", "configure") ? (
          <>
            <h2 className="crm-section-title">Lead dropdown options</h2>
            <TabFieldOptionsSettings orgId={orgId} entity="lead" />
          </>
        ) : null}
        <BoardDisplaySettingsPanel orgId={orgId} entity="lead" onSaved={setBoardDisplay} />
      </Drawer>
    </ListWorkspace>
  );
}
